/**
 * Period units and the `period_auto` format built on them.
 *
 * Import-free on purpose, so `yarn test:unit` (bare `node --test`) can load it —
 * `interpolationFormat.ts`, which re-exports everything here, value-imports
 * `@render/*` and cannot be. The number half is injected through
 * `PeriodValueOptions.formatCount` for the same reason.
 */

import type { PeriodFormatConfig, PeriodUnitNames } from './localeConfig'

export const PERIOD_UNITS = ['minute', 'day', 'week', 'month', 'year'] as const

export type PeriodUnit = (typeof PERIOD_UNITS)[number]

/**
 * The spellings a period-unit variable may hold, per canonical unit. Deliberately
 * the same vocabulary the seeded `period.in.*` translations key on — no invented
 * abbreviations, since the rendered word is the translation, not this table.
 *
 * Must cover every member of `PeriodUnits` (api/src/types/generic-enums.ts), the
 * enum an offer's `basePeriodUnit` / `trialPeriodUnit` holds — pinned by the spec.
 */
const PERIOD_UNIT_ALIASES: Readonly<Record<PeriodUnit, readonly string[]>> = {
  minute: ['minute', 'minutes'],
  day: ['day', 'days'],
  week: ['week', 'weeks'],
  month: ['month', 'months'],
  year: ['year', 'years'],
}

const PERIOD_UNIT_BY_ALIAS: ReadonlyMap<string, PeriodUnit> = new Map(
  (Object.entries(PERIOD_UNIT_ALIASES) as [PeriodUnit, readonly string[]][]).flatMap(
    ([unit, aliases]) => aliases.map((alias) => [alias, unit] as [string, PeriodUnit]),
  ),
)

/**
 * Resolve any accepted spelling of a period unit to its canonical singular form.
 *
 * @param value raw unit — a period-unit variable's value, or an author-picked override
 * @returns the canonical unit, or `null` when the value matches no known unit
 */
export function normalizePeriodUnit(value: unknown): PeriodUnit | null {
  if (value === null || value === undefined) return null
  const token = String(value).trim().toLowerCase()
  if (!token) return null
  return PERIOD_UNIT_BY_ALIAS.get(token) ?? null
}

export const PERIOD_VALUE_FORMATS = ['period_auto'] as const

export type PeriodValueFormat = (typeof PERIOD_VALUE_FORMATS)[number]

export function isPeriodValueFormat(
  format: string | null | undefined,
): format is PeriodValueFormat {
  return !!format && (PERIOD_VALUE_FORMATS as readonly string[]).includes(format)
}

export interface PeriodValueOptions {
  /** Runtime value of the configured period-unit variable (`periodunitvar`). */
  unitValue?: unknown
  /** Author-picked unit, which wins over `unitValue` whenever it resolves. */
  manualUnit?: string | null
  /** Drop the number when the count is exactly 1, leaving the unit word alone. */
  hideOne?: boolean | null
  /** Looks up `tr.global.period.in.<unit>` / `<unit>s`. */
  resolveTranslation: (key: string) => string | undefined
  /** Renders the count; defaults to `String(count)`. */
  formatCount?: (count: number) => string
  /**
   * The active locale's Period override. Its words win over `resolveTranslation`,
   * and its flags decide the separator and the gap. A derived config carries an
   * empty `units` and renders exactly what this formatter rendered before locale
   * overrides existed. Partial because the stored config is a raw JSON
   * passthrough, so a row written before a flag existed simply omits it.
   */
  period?: Partial<PeriodFormatConfig> | null
}

/** A blank override field means "not set", so it falls through to the translation. */
function overrideWord(names: Partial<PeriodUnitNames> | undefined, plural: boolean) {
  if (!names) return undefined
  const value = plural ? names.plural : names.singular
  return value && value.trim() ? value : undefined
}

/**
 * Format a numeric period as `<count> <unit word>` — `3 months`, `1 day`.
 *
 * The unit comes from `manualUnit` when it resolves, otherwise from `unitValue`;
 * both go through {@link normalizePeriodUnit}, so singular and plural spellings
 * collapse to one unit and the COUNT alone picks the rendered form.
 *
 * The word is resolved in one place, in order: the locale's Period override
 * ({@link PeriodValueOptions.period}), then the `tr.global.period.in.*`
 * translation the `period_singular` / `period_plural` formats already use, then
 * the English `<unit>` / `<unit>s`.
 *
 * `hideOne` is applied AFTER the word is resolved and knows nothing about where
 * the word came from, so an override cannot switch it off.
 *
 * Emitted in logical order — count, then unit — joined by a non-breaking space.
 * There is no `_rtl` "Force" counterpart and no isolation: a weak-LTR count
 * beside a strong-RTL word is already placed correctly by the browser's own
 * bidi algorithm under a `dir="rtl"` ancestor.
 *
 * @param value the count — non-numeric input is returned unchanged
 * @returns the assembled period, or the bare count when no unit resolves
 */
export function formatPeriodValue(value: unknown, opts: PeriodValueOptions): string {
  if (value === null || value === undefined || value === '') return ''
  const num = Number(value)
  if (!Number.isFinite(num)) return String(value)

  const count = opts.formatCount ? opts.formatCount(num) : String(num)
  const unit = normalizePeriodUnit(opts.manualUnit) ?? normalizePeriodUnit(opts.unitValue)
  if (!unit) return count

  const plural = num !== 1
  const word =
    overrideWord(opts.period?.units?.[unit], plural) ??
    opts.resolveTranslation(`tr.global.period.in.${unit}${plural ? 's' : ''}`) ??
    (plural ? `${unit}s` : unit)

  if (opts.hideOne && num === 1) return word

  const dot = opts.period?.dotAfterNumber ? '.' : ''
  const gap = opts.period?.spaceBetween === false ? '' : ' '
  return `${count}${dot}${gap}${word}`
}
